/**
 * Membership Stats "By family" section (WSRADM-0052 / WSRAPI-0158 B).
 *
 * One card per family of a chosen dimension, displaying current members,
 * MoM, YoY, churn %, moves, and a monthly stacked bar chart (covered + lapsed).
 * Server answers are rendered as sent — never averaged, summed, or divided.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart } from '@mui/x-charts/BarChart';
import { getDimensions } from '@/api/dimensions/dimensionsApi';
import type { StructureDimension } from '@/api/dimensions/types';
import { formatMonthLabel } from '@/api/reports/format';
import {
  fetchMembershipFamilies,
  isDimensionNotFound,
} from '@/api/reports/reportsApi';
import type {
  MembershipFamiliesMonthContent,
  MembershipFamiliesResponse,
  MembershipFamilyColumn,
  MembershipFamilyMetric,
} from '@/api/reports/types';
import { ColumnChooser, HiddenHint } from '@/components/ColumnChooser';
import { ErrorDetails } from '@/components/ErrorDetails';
import { familyColorOrNeutral } from '@/components/familyPalette';
import { ReportFreshness } from '@/components/ReportFreshness';
import { readScalarViewPref, writeScalarViewPref } from '@/config/viewPrefs';
import { useCopy } from '@/hooks/useCopy';
import { useHiddenKeys } from '@/hooks/useHiddenKeys';
import { useResource } from '@/hooks/useResource';

function formatSigned(val: number | null | undefined): string {
  if (val === null || val === undefined) return '—';
  if (val > 0) return `+${val}`;
  if (val < 0) return `−${Math.abs(val)}`;
  return '0';
}

function formatChurn(val: number | null | undefined, locale: string): string {
  if (val === null || val === undefined) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(val);
}

export function MembershipByFamilySection() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const familyDim = searchParams.get('familyDim') ?? '';
  const [dimensions, setDimensions] = useState<StructureDimension[] | null>(null);
  const [dimensionsForbidden, setDimensionsForbidden] = useState(false);

  // Seed familyDim from preference cookie once on mount if absent from URL.
  useEffect(() => {
    if (!searchParams.has('familyDim')) {
      const remembered = readScalarViewPref('membershipFamilyDim');
      if (remembered) {
        const next = new URLSearchParams(searchParams);
        next.set('familyDim', remembered);
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Load dimensions to populate the selector.
  // If 403 (unauthorized/narrow role), hide the selector without error.
  useEffect(() => {
    getDimensions()
      .then((res) => {
        setDimensions(res.dimensions.filter((d) => d.families && d.families.length >= 1));
      })
      .catch((err: unknown) => {
        const status =
          err && typeof err === 'object' && 'status' in err
            ? (err as { status?: number }).status
            : undefined;
        if (status === 403) {
          setDimensionsForbidden(true);
        } else {
          setDimensions([]);
        }
      });
  }, []);

  const resource = useResource(
    () => fetchMembershipFamilies({ dimension: familyDim }),
    [familyDim],
  );

  // Drop familyDim if the server reports DIMENSION_NOT_FOUND.
  useEffect(() => {
    if (resource.status !== 'error') return;
    if (isDimensionNotFound(resource.error) && familyDim !== '') {
      const next = new URLSearchParams(searchParams);
      next.delete('familyDim');
      setSearchParams(next, { replace: true });
    }
  }, [resource.status, resource.error, familyDim, searchParams, setSearchParams]);

  const onDimensionChange = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('familyDim', slug);
    setSearchParams(next);
    writeScalarViewPref('membershipFamilyDim', slug);
  };

  const data: MembershipFamiliesResponse | null = resource.data;
  const echoedSlug = data?.dimension?.slug ?? null;
  const echoedName = data?.dimension?.displayName ?? '';
  const familyColumns: MembershipFamilyColumn[] = data?.familyColumns ?? [];

  const familyViews = useHiddenKeys({
    param: 'hideFamilyCards',
    prefsKey: 'membershipFamilyCards',
    available: familyColumns.map((c) => c.slug),
  });

  const columnOptions = familyColumns.map((c) => {
    const palette = familyColorOrNeutral(c.color);
    return {
      key: c.slug,
      label: c.displayName,
      adornment: palette ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '2px',
            mr: 1,
            flexShrink: 0,
            bgcolor: palette.bg,
          }}
        />
      ) : undefined,
    };
  });

  const newestContent = data?.content && data.content.length > 0 ? data.content[0] : null;
  const currentCalendarMonth = new Date().toISOString().slice(0, 7);
  const isMonthToDate = newestContent?.month === currentCalendarMonth;

  const newestOverlap = newestContent?.overlapMembers ?? 0;

  const neverComputed = data?.neverComputed === true;

  return (
    <Box component="section" sx={{ mt: 5, mb: 4 }} data-testid="membership-by-family-section">
      <Typography variant="h2" sx={{ mb: 2 }}>
        {t('reports.byFamily.title', 'Members by family')}
      </Typography>

      {data ? (
        <ReportFreshness
          asOf={data.asOf}
          stale={data.stale}
          neverComputed={data.neverComputed}
          timezone={data.timezone}
          canRebuild={false}
          onRebuild={() => {}}
          rebuilding={false}
        />
      ) : null}

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        {!dimensionsForbidden && dimensions !== null && dimensions.length > 0 ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label={t('reports.byFamily.dimension', 'Dimension')}
            value={familyDim || (echoedSlug ?? '')}
            onChange={(_event, value: string | null) => {
              if (value === null) return;
              onDimensionChange(value);
            }}
          >
            {dimensions.map((option) => (
              <ToggleButton key={option.slug} value={option.slug}>
                {option.readOnly ? `${option.displayName} (From WStudio)` : option.displayName}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        ) : dimensionsForbidden && echoedName ? (
          <Typography variant="subtitle2" sx={{ alignSelf: 'center' }}>
            {echoedName}
          </Typography>
        ) : null}

        {data && !neverComputed && familyColumns.length > 0 ? (
          <ColumnChooser
            options={columnOptions}
            hidden={familyViews.hidden}
            onToggle={familyViews.toggle}
            onShowAll={familyViews.showAll}
            buttonLabel={t('reports.byFamily.families', 'Families')}
          />
        ) : null}
      </Stack>

      {!dimensionsForbidden && dimensions !== null && dimensions.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'reports.byFamily.noDimensions',
            'Create a grouping in Configuration to see members by family.',
          )}
        </Alert>
      ) : null}

      {resource.status === 'loading' ? (
        <Skeleton variant="rounded" height={220} role="status" sx={{ mb: 2 }} />
      ) : null}

      {resource.status === 'error' && !isDimensionNotFound(resource.error) ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('reports.loadError', 'Couldn’t load this report.')}
          <ErrorDetails
            intent="read"
            error={resource.error}
            headline={t('reports.loadError', 'Couldn’t load this report.')}
          />
        </Alert>
      ) : null}

      {neverComputed ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'reports.byFamily.neverComputed',
            'Family membership figures are built on the next report rollup.',
          )}
        </Alert>
      ) : null}

      {data ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(auto-fill, minmax(340px, 1fr))',
              },
              gap: 2,
            }}
          >
            {familyColumns.map((column) => {
              if (familyViews.hidden.has(column.slug)) return null;
              const metric: MembershipFamilyMetric | undefined = newestContent?.families.find(
                (f) => f.familySlug === column.slug,
              );
              const palette = familyColorOrNeutral(column.color);
              const familyColor = palette?.bg ?? theme.palette.primary.main;
              const lapsedColor = alpha(familyColor, 0.45);

              // Months oldest to newest for display
              const reversedContent: MembershipFamiliesMonthContent[] = [...data.content].reverse();
              const months = reversedContent.map((c) => c.month);
              const coveredData = reversedContent.map(
                (c) => c.families.find((f) => f.familySlug === column.slug)?.coveredMembers ?? 0,
              );
              const lapsedData = reversedContent.map(
                (c) => c.families.find((f) => f.familySlug === column.slug)?.lapsed ?? 0,
              );

              return (
                <Card variant="outlined" key={column.slug} data-testid={`family-card-${column.slug}`}>
                  <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {palette ? (
                          <Box
                            component="span"
                            aria-hidden
                            data-testid={`family-card-swatch-${column.slug}`}
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '2px',
                              mr: 1,
                              bgcolor: palette.bg,
                            }}
                          />
                        ) : null}
                        <Typography variant="h6" component="h3">
                          {column.displayName}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="h4" component="div">
                          {metric?.coveredMembers !== null && metric?.coveredMembers !== undefined
                            ? metric.coveredMembers
                            : '—'}
                          {isMonthToDate ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({t('reports.byFamily.monthToDate', 'month to date')})
                            </Typography>
                          ) : null}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t('reports.byFamily.mom', 'MoM {{momChange}}', {
                            momChange: formatSigned(metric?.momChange),
                          })}{' '}
                          ·{' '}
                          {t('reports.byFamily.yoy', 'YoY {{yoyChange}}', {
                            yoyChange: formatSigned(metric?.yoyChange),
                          })}{' '}
                          ·{' '}
                          {t('reports.byFamily.churn', 'Churn {{churnRate}}', {
                            churnRate: formatChurn(metric?.churnRate, i18n.language),
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            'reports.byFamily.moved',
                            'Moved in {{movedIn}} · out {{movedOut}}',
                            {
                              movedIn: metric?.movedIn ?? '—',
                              movedOut: metric?.movedOut ?? '—',
                            },
                          )}
                        </Typography>
                      </Box>

                      {!neverComputed && months.length > 0 ? (
                        <Box sx={{ height: 200 }}>
                          <BarChart
                            height={200}
                            xAxis={[
                              {
                                scaleType: 'band',
                                data: months,
                                valueFormatter: (m: string | null) =>
                                  m ? formatMonthLabel(m, i18n.language) : '',
                              },
                            ]}
                            series={[
                              {
                                id: `covered-${column.slug}`,
                                data: coveredData,
                                label: t('reports.byFamily.current', 'Current'),
                                color: familyColor,
                                stack: `stack-${column.slug}`,
                              },
                              {
                                id: `lapsed-${column.slug}`,
                                data: lapsedData,
                                label: t('reports.byFamily.lapsed', 'Lapsed (in grace)'),
                                color: lapsedColor,
                                stack: `stack-${column.slug}`,
                              },
                            ]}
                            margin={{ top: 10, bottom: 25, left: 40, right: 10 }}
                            slotProps={{
                              legend: {
                                direction: 'horizontal',
                                position: { vertical: 'top', horizontal: 'center' },
                              },
                            }}
                          />
                        </Box>
                      ) : null}
                    </CardContent>
                  </Card>
              );
            })}
          </Box>

          <HiddenHint
            count={familyViews.hiddenCount}
            kind="columns"
            onShowAll={familyViews.showAll}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.5 }}
          >
            {t(
              'reports.byFamily.movesNote',
              'Upgrades between families are shown as moves, not churn.',
            )}
          </Typography>

          {newestOverlap > 0 && newestContent ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
              data-testid="family-overlap-note"
            >
              {t(
                'reports.byFamily.overlapNote',
                '{{n}} members in {{month}} hold memberships in more than one family and are counted in each.',
                {
                  n: newestOverlap,
                  month: formatMonthLabel(newestContent.month, i18n.language),
                },
              )}
            </Typography>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
